/**
 * Das Sanctuary (GAME_DESIGN Abschnitt 5f).
 *
 * Der Rueckzugsort am eigenen Anchor. Er beantwortet die Frage, um die es in
 * diesem Spiel immer wieder geht:
 *
 *   > Bleibe ich an der Front - oder ziehe ich mich fuer Heilung und Einkauf
 *   > zurueck und lasse meine Army so lange ohne mich kaempfen?
 *
 * Damit das eine ENTSCHEIDUNG ist und kein Fluchtknopf, kostet jeder Schritt
 * Zeit: der Weg dorthin (rund sieben Sekunden), drei Sekunden hinein, zwei
 * hinaus - und die ganze Zeit ueber kaempft die eigene Armee allein. Seit dem
 * 09.10.2026 kostet es zusaetzlich Gold und Erfahrung: Wer nicht an der Front
 * steht, verdient nichts (Abschnitt 5c).
 *
 * **Ein Tor und ein Raum (Produktentscheidung vom 09.10.2026).**
 *
 * Bis dahin war das Sanctuary eine Flaeche auf der Lane: Man lief darauf und
 * blieb stehen. Im Spiel sah das aus, als stuende der Hero grundlos am Rand
 * herum - mitten im Geschehen und doch unantastbar.
 *
 * Jetzt ist es ein Durchgang. Man laeuft in das Tor, wartet den Channel ab
 * und wird in einen Raum NEBEN der Lane versetzt. Dort laeuft man frei
 * herum, kauft ein und heilt; durch dasselbe Tor geht es zurueck - wieder
 * mit Wartezeit.
 *
 * **Schaden bricht das Betreten nicht mehr ab (Produktentscheidung vom
 * 16.10.2026).**
 *
 * Robin: "Die Strafe, dass man 5 Sekunden nicht am Kampf teilnehmen kann,
 * reicht aus." Vorher kam, wer verfolgt wurde, gar nicht mehr hinein - ein
 * einziger Treffer warf den Channel um, und der Verfolger bestimmte, ob
 * man sich zurueckziehen darf.
 *
 * Der Preis steht jetzt woanders: Wer hineingeht, kann in dieser Zeit
 * NICHT automatisch angreifen (darfAutoAngreifen) - er steht drei Sekunden
 * lang verwundbar da und tut nichts. Zusammen mit den zwei Sekunden hinaus
 * sind das fuenf Sekunden ohne Kampf.
 *
 * Was man selbst tut, zaehlt weiter: Ein Skill laesst die Zeit von vorn
 * beginnen, solange man im Tor steht. Sonst koennte man aus dem Tor heraus
 * zaubern und trotzdem puenktlich verschwinden.
 *
 * **Beide Tore funktionieren gleich (Produktentscheidung vom 12.10.2026).**
 * Man stellt sich hinein und bleibt stehen; geht man weg, ist es abgebrochen
 * und faengt beim naechsten Mal von vorn an. Bis dahin war der Rueckweg ein
 * Einbahnweg: Einmal hineingetreten, stand der Hero fest und ging hinaus,
 * ob er wollte oder nicht.
 *
 * Fuenf Zustaende, und diese Datei kennt die Uebergaenge dazwischen:
 *
 *   draussen  -> betritt    sobald er im Tor steht
 *   betritt   -> drinnen    wenn der Channel durchlaeuft (dann: Versetzen)
 *   betritt   -> draussen   wenn er weggeht - und nur dann
 *   betritt   -> betritt    bei eigenem Angriff: die Zeit faengt von vorn an
 *   drinnen   -> verlaesst  sobald er im Tor des Raums steht
 *   verlaesst -> drinnen    wenn er aus dem Tor geht (12.10.2026)
 *   verlaesst -> blockiert  wenn die Zeit um ist (dann: Versetzen)
 *   blockiert -> draussen   sobald er das Tor verlassen hat
 *
 * Der fuenfte Zustand ist der unscheinbarste und der wichtigste: Wer
 * herauskommt, steht im Tor. Ohne "blockiert" betraete er es im selben Takt
 * wieder und kaeme nie mehr heraus.
 *
 * Die Datei rechnet nur mit Zahlen und einem Zustand - kein Match, keine
 * Creeps. Dadurch bleibt sie ohne laufendes Spiel pruefbar.
 */

import {
  SANCTUARY_ENTRY_SECONDS,
  SANCTUARY_EXIT_SECONDS,
  SANCTUARY_POSITION,
  SANCTUARY_RADIUS,
  SANCTUARY_ROOM,
  SANCTUARY_ROOM_ENTRY,
  SANCTUARY_ROOM_GATE,
} from '@dueline/game-data';
import type { PlayerSide, Vec2 } from '@dueline/shared';

/** Wo jemand im Ablauf gerade steht. */
export type SanctuaryZustand = 'draussen' | 'betritt' | 'drinnen' | 'verlaesst' | 'blockiert';

/** Steht diese Position im Tor auf der Lane? */
export function istImTor(position: Vec2, side: PlayerSide): boolean {
  const mitte = SANCTUARY_POSITION[side];
  return Math.hypot(position.x - mitte.x, position.y - mitte.y) <= SANCTUARY_RADIUS;
}

/** Steht diese Position im Tor INNERHALB des Raums - also auf dem Rueckweg? */
export function istImRaumTor(position: Vec2, side: PlayerSide): boolean {
  const mitte = SANCTUARY_ROOM_GATE[side];
  return Math.hypot(position.x - mitte.x, position.y - mitte.y) <= SANCTUARY_RADIUS;
}

/** Die Grenzen des Raums - innerhalb davon darf man sich frei bewegen. */
export function raumGrenzen(side: PlayerSide) {
  return SANCTUARY_ROOM[side];
}

/** Wo man im Raum ankommt. */
export function raumEingang(side: PlayerSide): Vec2 {
  return SANCTUARY_ROOM_ENTRY[side];
}

/** Wo man auf der Lane wieder auftaucht. */
export function torAusgang(side: PlayerSide): Vec2 {
  return SANCTUARY_POSITION[side];
}

/** Haelt eine Position innerhalb des Raums. */
export function haltImRaum(position: Vec2, side: PlayerSide): Vec2 {
  const grenzen = SANCTUARY_ROOM[side];
  return {
    x: Math.min(grenzen.maxX, Math.max(grenzen.minX, position.x)),
    y: Math.min(grenzen.maxY, Math.max(grenzen.minY, position.y)),
  };
}

export interface SanctuaryEingabe {
  /** Zustand aus dem letzten Takt. */
  zustand: SanctuaryZustand;
  /** Restzeit des laufenden Channels oder des Verlassens, in Sekunden. */
  rest: number;
  /** Dauer dieses Takts. */
  dt: number;

  /** Steht der Hero gerade im Tor auf der Lane? */
  imTor: boolean;
  /** Steht er im Tor innerhalb des Raums? */
  imRaumTor: boolean;
  /** Lebt er? Ein toter Hero ist nirgends. */
  alive: boolean;

  /**
   * Hat er selbst etwas Aggressives getan?
   *
   * Seit dem 16.10.2026 der EINZIGE Grund, aus dem das Betreten nicht
   * durchlaeuft - und auch der bricht nicht ab, sondern faengt die Zeit
   * von vorn an. Schaden von aussen zaehlt gar nicht mehr; das Feld
   * "getroffen" ist deshalb ersatzlos weg.
   */
  selbstAngegriffen: boolean;
}

export interface SanctuaryErgebnis {
  zustand: SanctuaryZustand;
  rest: number;
  /**
   * Wohin der Hero in diesem Takt versetzt wird - null heisst: nirgendwohin.
   *
   * Das Versetzen gehoert in die Regel und nicht in den Aufrufer: Es ist Teil
   * des Uebergangs. Wer es vergaesse, haette einen Hero, der im Zustand
   * "drinnen" mitten auf der Lane steht.
   */
  versetzeNach: Vec2 | null;
}

function ohneVersatz(zustand: SanctuaryZustand, rest: number): SanctuaryErgebnis {
  return { zustand, rest, versetzeNach: null };
}

/**
 * Ein Takt des Ablaufs.
 *
 * Die Reihenfolge der Pruefungen ist die Regel: Zuerst alles, was abbricht,
 * dann erst, was weitergeht. Andersherum koennte ein Hero den Channel in
 * demselben Takt abschliessen, in dem ihn ein Treffer haette unterbrechen
 * muessen.
 */
export function stepSanctuary(eingabe: SanctuaryEingabe, side: PlayerSide): SanctuaryErgebnis {
  const dt = Number.isFinite(eingabe.dt) && eingabe.dt > 0 ? eingabe.dt : 0;

  /*
   * Tot ist ueberall draussen - auch mitten im Channel und auch im Raum.
   *
   * Das Versetzen zurueck ans Tor gehoert dazu: Ein gefallener Hero darf
   * nicht im Raum liegen bleiben, sonst erschiene er dort wieder und waere
   * sofort unangreifbar.
   */
  if (!eingabe.alive) {
    const warDrin = eingabe.zustand === 'drinnen' || eingabe.zustand === 'verlaesst';
    return {
      zustand: 'draussen',
      rest: 0,
      versetzeNach: warDrin ? torAusgang(side) : null,
    };
  }

  switch (eingabe.zustand) {
    case 'draussen': {
      if (!eingabe.imTor) return ohneVersatz('draussen', 0);
      /*
       * Das Betreten beginnt von selbst, sobald er im Tor steht - es braucht
       * keinen eigenen Knopf. Das Tor liegt abseits der Laufspur; wer dort
       * hineinlaeuft, will hindurch.
       */
      return ohneVersatz('betritt', SANCTUARY_ENTRY_SECONDS);
    }

    case 'betritt': {
      /*
       * Nur noch ein Abbruchgrund: weggehen (16.10.2026).
       *
       * Schaden zaehlt nicht mehr - sonst entschiede der Verfolger, ob man
       * sich zurueckziehen darf.
       */
      if (!eingabe.imTor) return ohneVersatz('draussen', 0);

      /*
       * Ein eigener Angriff wirft nicht hinaus, sondern setzt die Zeit
       * zurueck: Wer im Tor steht und zaubert, faengt von vorne an.
       * Robins Wortlaut: "bricht die Zeit wieder ab und startet neu,
       * solange man noch auf dem Punkt steht."
       */
      if (eingabe.selbstAngegriffen) {
        return ohneVersatz('betritt', SANCTUARY_ENTRY_SECONDS);
      }

      const rest = eingabe.rest - dt;
      if (rest > 0) return ohneVersatz('betritt', rest);
      // Durch: Jetzt - und nur jetzt - wird er in den Raum versetzt.
      return { zustand: 'drinnen', rest: 0, versetzeNach: raumEingang(side) };
    }

    case 'drinnen': {
      /*
       * Drinnen laeuft er frei herum. Der Rueckweg beginnt erst, wenn er in
       * das Tor des Raums hineinlaeuft - bis dahin kann er einkaufen und
       * heilen, so lange er will.
       */
      if (!eingabe.imRaumTor) return ohneVersatz('drinnen', 0);
      return ohneVersatz('verlaesst', SANCTUARY_EXIT_SECONDS);
    }

    case 'verlaesst': {
      /*
       * Weggehen bricht ab - genau wie beim Betreten
       * (Produktentscheidung vom 12.10.2026).
       *
       * Vorher war der Rueckweg ein Einbahnweg: Einmal ins Tor getreten,
       * stand der Hero fest und ging hinaus, ob er wollte oder nicht.
       * Robins Rueckmeldung: Das Verlassen soll sich anfuehlen wie das
       * Betreten - man steht im Tor, und man kann es sich anders
       * ueberlegen.
       *
       * Die Zeit faengt beim naechsten Mal von VORNE an (rest 0). Eine
       * pausierende Zeit waere eine zweite, eigene Regel gewesen; so gilt
       * fuer beide Tore dieselbe.
       */
      if (!eingabe.imRaumTor) return ohneVersatz('drinnen', 0);

      const rest = eingabe.rest - dt;
      if (rest > 0) return ohneVersatz('verlaesst', rest);
      // Zurueck auf die Lane - und erst einmal gesperrt, siehe unten.
      return { zustand: 'blockiert', rest: 0, versetzeNach: torAusgang(side) };
    }

    case 'blockiert': {
      /*
       * Er steht gerade im Tor, weil er eben dort herausgekommen ist. Ohne
       * diesen Zustand betraete er es im selben Takt wieder - und kaeme nie
       * mehr heraus (im Spiel aufgefallen am 09.10.2026, damals noch mit
       * der alten Flaeche).
       */
      if (eingabe.imTor) return ohneVersatz('blockiert', 0);
      return ohneVersatz('draussen', 0);
    }
  }
}

/**
 * Ist der Hero gerade aus dem Kampf genommen?
 *
 * Gilt fuer BEIDE Richtungen und fuer alles: nicht angreifbar, nicht
 * anvisierbar, fuer Creeps irrelevant - und er selbst kann auch nichts.
 *
 * Das Verlassen zaehlt noch dazu: Die zwei Sekunden sind der Preis dafuer,
 * dass man drinnen sicher war. Waere man dabei schon angreifbar UND
 * handlungsunfaehig, waere das Verlassen eine Falle statt eines Preises.
 *
 * "blockiert" zaehlt NICHT dazu: Da steht er wieder auf der Lane und ist
 * angreifbar wie jeder andere.
 */
export function istGeschuetzt(zustand: SanctuaryZustand): boolean {
  return zustand === 'drinnen' || zustand === 'verlaesst';
}

/**
 * Darf er von sich aus zuschlagen (Produktentscheidung vom 16.10.2026)?
 *
 * Im Raum ohnehin nicht - dort ist er aus dem Kampf. Neu ist das BETRETEN:
 * Wer hineingeht, laesst die Waffe unten. Das ist der Preis dafuer, dass
 * ihn niemand mehr aus dem Channel schlagen kann.
 *
 * Ohne diese Regel waere das Betreten ein geschenkter Vorteil: drei
 * Sekunden unangreifbar im Ablauf UND weiter Schaden austeilen.
 *
 * Gemeint ist nur der AUTOMATISCHE Angriff. Einen Skill darf er wirken -
 * er faengt sich damit nur die Zeit von vorne ein (siehe stepSanctuary).
 */
export function darfAutoAngreifen(zustand: SanctuaryZustand): boolean {
  return zustand === 'draussen' || zustand === 'blockiert';
}

/** Ist er gerade im Raum neben der Lane - also nicht mehr auf dem Feld? */
export function istImRaum(zustand: SanctuaryZustand): boolean {
  return zustand === 'drinnen' || zustand === 'verlaesst';
}

/*
 * HIER STAND "darfLaufen" (bis zum 12.10.2026).
 *
 * Die Funktion sperrte die Bewegung waehrend des Rueckwegs. Seit das
 * Verlassen abbrechbar ist, gibt es keinen Zustand mehr, in dem das
 * Sanctuary das Laufen verbietet - und eine Funktion, die immer "ja" sagt,
 * ist keine Regel, sondern eine Falle fuer den naechsten Leser.
 *
 * Dass das Sanctuary niemanden festhaelt, ist trotzdem geprueft: in
 * sanctuary.test.ts ueber heroDarfLaufen, also an der Stelle, an der die
 * Bewegung wirklich entschieden wird.
 */

/**
 * Darf er von hier aus einkaufen (Abschnitt 5f)?
 *
 * Nur drinnen - nicht schon beim Betreten und nicht mehr beim Verlassen.
 * Der Shop ist der Grund, warum man den Weg auf sich nimmt.
 */
export function darfEinkaufen(zustand: SanctuaryZustand): boolean {
  return zustand === 'drinnen';
}
